import { call, put, select } from 'redux-saga/effects';

import { goToBoard } from './router';
import request from '../request';
import selectors from '../../../selectors';
import actions from '../../../actions';
import api from '../../../api';
import { transformCard } from '../../../api/cards';
import ActionTypes from '../../../constants/ActionTypes';
import { createLocalId } from '../../../utils/local-id';

export function* createList(boardId, data) {
  const nextData = {
    ...data,
    position: yield select(selectors.selectNextListPosition, boardId),
  };

  const localId = yield call(createLocalId);

  yield put(
    actions.createList({
      ...nextData,
      boardId,
      id: localId,
    }),
  );

  let list;
  try {
    ({ item: list } = yield call(request, api.createList, boardId, nextData));
  } catch (error) {
    yield put(actions.createList.failure(localId, error));
    return;
  }

  yield put(actions.createList.success(localId, list));
}

export function* createListInCurrentBoard(data) {
  const { boardId } = yield select(selectors.selectPath);

  yield call(createList, boardId, data);
}

export function* handleListCreate(list) {
  yield put(actions.handleListCreate(list));
}

export function* updateList(id, data) {
  yield put(actions.updateList(id, data));

  const isTransfer = data && data.boardId != null;

  let list;
  let included = {};
  try {
    ({ item: list, included = {} } = yield call(request, api.updateList, id, data));
  } catch (error) {
    yield put(actions.updateList.failure(id, error));
    return null;
  }

  yield put(actions.updateList.success(list));

  if (isTransfer) {
    yield call(applyListPointIncluded, included || {});
  }

  return { list, included: included || {} };
}

export function* handleListUpdate(list, included = {}) {
  yield put(actions.handleListUpdate(list));

  if (
    included &&
    ((included.cardPointData && included.cardPointData.length > 0) ||
      (included.listReportData && included.listReportData.length > 0) ||
      (included.cardPointAdvancedData && included.cardPointAdvancedData.length > 0) ||
      (included.cardPointArchivedData && included.cardPointArchivedData.length > 0))
  ) {
    yield call(applyListPointIncluded, included);
  }
}

export function* moveList(id, index) {
  const { boardId } = yield select(selectors.selectListById, id);
  const position = yield select(selectors.selectNextListPosition, boardId, index, id);

  yield call(updateList, id, {
    position,
  });
}

export function* prepareTransferList(id, boardId) {
  yield put(actions.prepareTransferList(id, boardId));

  let items;
  let pointSystem = null;
  try {
    ({ items, pointSystem } = yield call(request, api.checkListTransfer, id, boardId));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[prepareTransferList] transfer-check failed:', error);
    yield put(actions.prepareTransferList.failure(id, boardId, error));
    return;
  }

  yield put(actions.prepareTransferList.success(id, boardId, items || [], pointSystem || null));
}

export function* transferList(id, boardId, index, addUserIds = [], pointSystemOptions = {}) {
  let position = yield select(selectors.selectNextListPosition, boardId, index, id);

  // Если целевая доска ещё без списков в ORM — ставим в начало.
  if (typeof position !== 'number' || Number.isNaN(position)) {
    position = 65535;
  }

  const data = {
    boardId,
    position,
  };

  if (Array.isArray(addUserIds) && addUserIds.length > 0) {
    data.addUserIds = addUserIds.map(String);
  }

  if (pointSystemOptions.enableAndApplyTemplateId) {
    data.enableAndApplyTemplateId = String(pointSystemOptions.enableAndApplyTemplateId);
  }

  const result = yield call(updateList, id, data);
  if (!result) {
    return;
  }

  // После transfer всегда уходим на целевую доску — fetchBoard подтянет rebind'нутые данные.
  yield call(goToBoard, boardId);
}

export function* confirmListTransferPointSystem(enable) {
  const prompt = yield select(selectors.selectListTransferPointSystemPrompt);
  if (!prompt || prompt.mode !== 'enable_and_apply') {
    return;
  }

  const {
    listId,
    boardId,
    index = 0,
    addUserIds = [],
    templateId,
    templateName,
  } = prompt;

  yield put(actions.clearListTransferPointSystemPrompt());

  if (enable) {
    yield call(transferList, listId, boardId, index, addUserIds, {
      enableAndApplyTemplateId: templateId,
    });
    return;
  }

  yield call(transferList, listId, boardId, index, addUserIds, {
    skipCheck: true,
  });
}

function* applyListPointIncluded(included = {}) {
  if (included.board) {
    yield put(actions.handleBoardUpdate(included.board));
  }

  const {
    cardPointData = [],
    cardPointAdvancedData = [],
    cardPointArchivedData = [],
    listReportData = [],
  } = included;

  for (const item of listReportData) {
    if (item) {
      yield put(actions.fetchListReportData.success(item.listId, item));
    }
  }

  for (const item of cardPointData) {
    if (item && item.pointSistemId) {
      yield put(actions.fetchCardPointData.success(item.cardId, item.pointSistemId, item));
    }
  }

  if (cardPointAdvancedData.length > 0) {
    const cardPointAdvancedDataByCardId = {};
    cardPointAdvancedData.forEach((item) => {
      if (!item) {
        return;
      }

      if (!cardPointAdvancedDataByCardId[item.cardId]) {
        cardPointAdvancedDataByCardId[item.cardId] = [];
      }

      cardPointAdvancedDataByCardId[item.cardId].push(item);
    });

    for (const cardId of Object.keys(cardPointAdvancedDataByCardId)) {
      const items = cardPointAdvancedDataByCardId[cardId];
      const pointSystemId = items[0]?.pointSistemId;

      if (pointSystemId) {
        yield put(actions.fetchCardPointAdvancedData.success(cardId, pointSystemId, items));
      }
    }
  }

  for (const item of cardPointArchivedData) {
    if (item) {
      yield put({
        type: ActionTypes.CARD_POINT_DATA_COMPLETE__SUCCESS,
        payload: {
          archivedItem: item,
        },
      });
    }
  }
}

function* applyListCloneResponse(response) {
  const { item: list, included = {} } = response;
  const {
    labels = [],
    cards = [],
    tasks = [],
    cardMemberships = [],
    cardLabels = [],
  } = included;

  if (list) {
    yield put(actions.handleListCreate(list));
  }

  for (const label of labels) {
    if (label) {
      yield put(actions.handleLabelCreate(label));
    }
  }

  for (const card of cards) {
    if (card) {
      yield put(actions.handleCardCreate(transformCard(card)));
    }
  }

  for (const task of tasks) {
    if (task) {
      yield put(actions.handleTaskCreate(task));
    }
  }

  for (const cardMembership of cardMemberships) {
    if (cardMembership) {
      yield put(actions.handleUserToCardAdd(cardMembership));
    }
  }

  for (const cardLabel of cardLabels) {
    if (cardLabel) {
      yield put(actions.handleLabelToCardAdd(cardLabel));
    }
  }

  yield call(applyListPointIncluded, included);
}

export function* cloneList(id, boardId, navigateAfterClone = false, addUserIds = []) {
  const data = {
    boardId,
  };

  if (Array.isArray(addUserIds) && addUserIds.length > 0) {
    data.addUserIds = addUserIds.map(String);
  }

  let response;
  try {
    response = yield call(request, api.cloneList, id, data);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[cloneList] clone failed:', error);
    return;
  }

  // Socket-события не приходят инициатору запроса (исключается в broadcast),
  // поэтому применяем ответ API к store напрямую.
  yield call(applyListCloneResponse, response);

  if (navigateAfterClone) {
    yield call(goToBoard, boardId);
  }
}

export function* deleteList(id) {
  yield put(actions.deleteList(id));

  let list;
  try {
    ({ item: list } = yield call(request, api.deleteList, id));
  } catch (error) {
    yield put(actions.deleteList.failure(id, error));
    return;
  }

  yield put(actions.deleteList.success(list));
}

export function* handleListDelete(list) {
  yield put(actions.handleListDelete(list));
}

export function* archiveList(id) {
  yield put(actions.archiveList(id));

  let list;
  try {
    ({ item: list } = yield call(request, api.archiveList, id));
  } catch (error) {
    yield put(actions.archiveList.failure(id, error));
    return;
  }

  yield put(actions.archiveList.success(list));
}

export function* handleListArchive(list) {
  yield put(actions.handleListArchive(list));
}

export function* restoreList(id) {
  yield put(actions.restoreList(id));

  let list;
  try {
    ({ item: list } = yield call(request, api.restoreList, id));
  } catch (error) {
    yield put(actions.restoreList.failure(id, error));
    return;
  }

  yield put(actions.restoreList.success(list));
}

export function* handleListRestore(list) {
  yield put(actions.handleListRestore(list));
}


export default {
  createList,
  createListInCurrentBoard,
  handleListCreate,
  updateList,
  handleListUpdate,
  moveList,
  prepareTransferList,
  transferList,
  confirmListTransferPointSystem,
  cloneList,
  deleteList,
  handleListDelete,
  archiveList,
  handleListArchive,
  handleListRestore,
  restoreList,
};
